import React, { useEffect, useState } from "react";
import { Button, Card, DatePicker, Input, message, Modal, Select, Space, Table, Tag, Tooltip } from "antd";
import {
  EyeOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { isLoaded, useFirestore, useFirestoreConnect } from "react-redux-firebase";
import * as dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { FIRESTORE_SHIPMENTS_TABLE } from "constants/FirestoreConstant";
import locale from "antd/es/date-picker/locale/nl_NL";

const { Option } = Select;
const { RangePicker } = DatePicker;

const SHIPMENT_STATUS_COLORS = {
  CREATED: "default",
  PICKED_UP: "blue",
  IN_TRANSIT: "orange",
  DELIVERED: "green",
  CANCELLED: "red",
};

const SHIPMENT_STATUS_LABELS = {
  CREATED: "Aangemaakt",
  PICKED_UP: "Opgehaald",
  IN_TRANSIT: "Onderweg",
  DELIVERED: "Geleverd",
  CANCELLED: "Geannuleerd",
};

const Shipments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const [messageApi, contextHolder] = message.useMessage();

  // Connect to Firestore shipments collection
  useFirestoreConnect([{ collection: FIRESTORE_SHIPMENTS_TABLE, orderBy: ["createdAt", "desc"] }]);
  const shipments = useSelector((state) => state.firestoreReducer.ordered.shipments) || [];

  const [filteredShipments, setFilteredShipments] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    applyFilters();
  }, [shipments, searchText, selectedStatus, dateRange]);

  const applyFilters = () => {
    let filtered = [...shipments];

    // Filter op status
    if (selectedStatus !== "All") {
      filtered = filtered.filter((s) => s.status?.current === selectedStatus);
    }

    // Filter op zoekterm (order ID, ontvanger, plaats)
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.orderId?.toLowerCase().includes(search) ||
          s.recipientName?.toLowerCase().includes(search) ||
          s.address?.city?.toLowerCase().includes(search),
      );
    }

    // Filter op datum range
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter((s) => {
        const deliveryDate = dayjs(s.deliveryDate, "YYYY-MM-DD");
        return deliveryDate.isBetween(start, end, "day", "[]");
      });
    }

    // Sorteer op datum (nieuwste eerst)
    filtered.sort((a, b) => b.createdAt - a.createdAt);

    setFilteredShipments(filtered);
  };

  const handleCancelShipment = (shipment) => {
    Modal.confirm({
      title: t("shipment.cancel_confirm_title"),
      icon: <ExclamationCircleOutlined />,
      content: t("shipment.cancel_confirm_message", { orderId: shipment.orderId }),
      okText: t("yes"),
      cancelText: t("no"),
      okType: "danger",
      onOk: async () => {
        try {
          const now = Math.floor(Date.now() / 1000);
          const prevCurrent = shipment.status?.current || "CREATED";
          const prevHistory = shipment.status?.history || [];

          const nextStatus = {
            current: "CANCELLED",
            history: [
              ...prevHistory,
              {
                from: prevCurrent,
                to: "CANCELLED",
                at: now,
                source: "cms",
              },
            ],
          };

          await firestore.collection(FIRESTORE_SHIPMENTS_TABLE).doc(shipment.id).update({
            status: nextStatus,
            updatedAt: Date.now(),
          });
          messageApi.success(t("shipment.cancelled_success"));
        } catch (error) {
          messageApi.error(t("shipment.cancel_failed"));
        }
      },
    });
  };

  const tableColumns = [
    {
      title: t("shipment.svz_shipment_id"),
      dataIndex: "svzShipmentId",
      render: (_, shipment) => (
        <a
          onClick={(e) => {
            e.preventDefault();
            navigate(`/cms/shipments/${shipment.id}`);
          }}
        >
          {shipment.svzShipmentId}
        </a>
      ),
    },
    {
      title: t("order.order_id"),
      dataIndex: "orderId",
      render: (orderId) => <span>{orderId}</span>,
    },
    {
      title: t("shipment.status"),
      dataIndex: "status",
      render: (status) => (
        <Tag color={SHIPMENT_STATUS_COLORS[status?.current] || "default"}>
          {SHIPMENT_STATUS_LABELS[status?.current] || status?.current}
        </Tag>
      ),
    },
    {
      title: t("shipment.recipient"),
      dataIndex: "recipientName",
      render: (name) => <span>{name}</span>,
    },
    {
      title: t("shipment.city"),
      dataIndex: "address",
      render: (address) => <span>{address?.city || "-"}</span>,
    },
    {
      title: t("shipment.delivery_date"),
      dataIndex: "deliveryDate",
      render: (date) => <span>{date ? dayjs(date).format(DATE_FORMAT_DD_MM_YYYY) : "-"}</span>,
    },
    {
      title: t("shipment.tracking"),
      dataIndex: "trackingUrl",
      render: (url) =>
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            📦 Track & Trace
          </a>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      title: t("shipment.created_at"),
      dataIndex: "createdAt",
      render: (timestamp) => <span>{dayjs(timestamp).format(DATE_FORMAT_DD_MM_YYYY)}</span>,
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, shipment) => (
        <Space>
          <Tooltip title={t("details")}>
            <Button type="text" icon={<EyeOutlined />} onClick={() => navigate(`/cms/shipments/${shipment.id}`)} />
          </Tooltip>
          {shipment.status !== "DELIVERED" && shipment.status !== "CANCELLED" && (
            <>
              <Tooltip title={t("edit")}>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/cms/shipments/${shipment.id}/edit`)}
                />
              </Tooltip>
              <Tooltip title={t("shipment.cancel")}>
                <Button
                  type="text"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleCancelShipment(shipment)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Space wrap>
            <Input
              value={searchText}
              placeholder={t("shipment.search_placeholder")}
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
            <Select
              value={selectedStatus}
              style={{ minWidth: 180 }}
              onChange={setSelectedStatus}
              placeholder={t("status")}
            >
              <Option value="All">{t("all")}</Option>
              {Object.keys(SHIPMENT_STATUS_LABELS).map((status) => (
                <Option key={status} value={status}>
                  {SHIPMENT_STATUS_LABELS[status]}
                </Option>
              ))}
            </Select>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              locale={locale}
              placeholder={[t("start_date"), t("end_date")]}
              format={DATE_FORMAT_DD_MM_YYYY}
            />
          </Space>
        </div>

        <Table
          columns={tableColumns}
          dataSource={filteredShipments}
          rowKey="id"
          loading={!isLoaded(shipments)}
          scroll={{ x: 1200 }}
        />
      </Card>
    </>
  );
};

export default Shipments;
